import React, { useState, useCallback, useEffect } from 'react';
import ActionsSection from 'components/Form/ActionsSection';
import KeyboardSmall from 'components/keyboard-small';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { profileSelectors } from 'store/profile';
import { StyledModalContainer, StyledModalTitle, StyledBlock } from './StyledComponents';
import { useSelector } from 'react-redux';

const SECTION_NAME = 'PIN_MODAL_INPUT';
const MAX_PIN_LENGTH = 4;

const SECTION_CONFIG = {
  leaveFor: {
    right: '@KEYBOARD_NUM',
    up: '',
    left: '',
    down: '',
  },
};

const KEYBOARD_CONFIG = {
  leaveFor: {
    left: `@${SECTION_NAME}`,
    right: '',
    up: '',
    down: '',
  },
};

interface IEnterPinModal {
  onModalBack: () => void;
  onModalConfirm: () => void;
}

const EnterPinModal: React.FC<IEnterPinModal> = ({ onModalBack, onModalConfirm }) => {
  const userscurrentPinCode = useSelector(profileSelectors.selectPin);
  const [pin, setPin] = useState('');
  const [isPinValid, setIsPinValid] = useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(true);
  const title = 'Enter pin';

  const handleConfirmation = useCallback(() => {
    if (pin === userscurrentPinCode) {
      onModalConfirm();
    } else {
      setIsPinValid(false);
    }
  }, [onModalConfirm, pin, userscurrentPinCode]);

  const isPinComplete = useCallback(() => pin.length === MAX_PIN_LENGTH, [pin]);

  const showKeyboard = () => {
    setIsKeyboardVisible(true);
    setIsPinValid(true);
    SpatialNavigationWrapper.focus('@KEYBOARD_NUM');
  };

  const hideKeyboard = () => setIsKeyboardVisible(false);

  const onKeyboardInput = (value) => {
    if (value === 'remove') {
      setPin((v) => v.slice(0, -1));
    } else if (value === 'close') {
      setIsKeyboardVisible(false);
      setIsPinValid(isPinComplete());

      handleConfirmation();

      SpatialNavigationWrapper.focus(`@${SECTION_NAME}`);
    } else if (!isPinComplete()) {
      setPin((v) => v + value);
    }
  };

  useEffect(() => {
    if (isPinComplete()) {
      handleConfirmation();
    }
  }, [handleConfirmation, isPinComplete, pin]);

  return (
    <StyledModalContainer>
      <StyledModalTitle>{title}</StyledModalTitle>
      <StyledBlock>
        <ActionsSection
          id={SECTION_NAME}
          sectionName={SECTION_NAME}
          defaultSection
          config={SECTION_CONFIG}
        >
          <ActionsSection.PinInput
            value={pin}
            isValid={isPinValid}
            invalidMessage={'PIN is not valid'}
            cursor={isKeyboardVisible}
            onEnter={showKeyboard}
          />
          <ActionsSection.Button label={'Cancel'} onEnter={onModalBack} onFocus={hideKeyboard} />
        </ActionsSection>
        {isKeyboardVisible && (
          <KeyboardSmall
            className="keyboard-small__pin"
            onEnter={onKeyboardInput}
            config={KEYBOARD_CONFIG}
          />
        )}
      </StyledBlock>
    </StyledModalContainer>
  );
};

export default EnterPinModal;
